"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetSearchQueryDetails = exports.googleAdsGetSearchQueryDetailsResultSchema = exports.googleAdsGetSearchQueryDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetSearchQueryDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific Search campaign ID to analyze. If not provided, returns data for all Search campaigns in the account.'),
    query: zod_1.z
        .string()
        .nullish()
        .describe('Optional: Search query text to filter results (e.g., "shoes" finds queries containing "shoes"). Leave empty to return all search queries.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. If omitted, returns lifetime data.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. If omitted, returns data up to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of search queries to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(true)
        .describe('Fail if no search query data is found. Unless specified, defaults to true.'),
})
    .describe('Get actual search queries that triggered your Google Ads in Search campaigns. Shows what users actually searched for.');
exports.googleAdsGetSearchQueryDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetSearchQueryDetailsInputSchema);
var googleAdsGetSearchQueryDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    query: zod_1.z.string().describe('The actual search query that triggered the ad'),
    keyword: zod_1.z.string().nullish().describe('The keyword that matched this query'),
    matchType: zod_1.z.enum(['BROAD', 'PHRASE', 'EXACT']).nullish().describe('Match type of the keyword'),
    adGroup: zod_1.z.string().nullish().describe('Ad group name'),
    campaign: zod_1.z.string().nullish().describe('Campaign name'),
    currencyCode: zod_1.z
        .string()
        .nullish()
        .describe('Currency code for cost metrics (e.g., USD, EUR, GBP)'),
    metrics: zod_1.z.object({
        impressions: zod_1.z.number().describe('Number of impressions for this query'),
        clicks: zod_1.z.number().describe('Number of clicks for this query'),
        ctr: zod_1.z.number().describe('Click-through rate (CTR)'),
        averageCpc: zod_1.z.number().describe('Average cost per click in account currency'),
        cost: zod_1.z.number().describe('Total cost in account currency'),
        conversions: zod_1.z.number().describe('Number of conversions from this query'),
        conversionValue: zod_1.z
            .number()
            .nullish()
            .describe('Total conversion value in account currency (if conversion value tracking is enabled)'),
        valuePerConversion: zod_1.z
            .number()
            .nullish()
            .describe('Average value per conversion in account currency'),
        roas: zod_1.z
            .number()
            .nullish()
            .describe('Return on ad spend (ROAS) - calculated as conversion_value / cost'),
    }),
}));
exports.googleAdsGetSearchQueryDetailsResultSchema = googleAdsGetSearchQueryDetailsResponseSchema;
exports.googleAdsGetSearchQueryDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetSearchQueryDetails',
    displayName: 'Google Ads Search Query Performance',
    displayDescription: 'Get actual search queries that triggered your Google Ads in Search campaigns. Shows what users actually searched for, impressions, clicks, CTR, CPC, cost, conversions, and ROAS. Use this to discover new keyword opportunities and identify negative keyword candidates.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetSearchQueryDetailsArgsSchema,
}, googleAdsGetSearchQueryDetailsResponseSchema);
